import React, { useState, useEffect, useMemo } from 'react';
import './Admin.css';

/* ──────────────────────────────────────────────
   Helpers
────────────────────────────────────────────── */
function getToken() {
  try {
    const s = sessionStorage.getItem('supabase-auth-token');
    return s ? (JSON.parse(s).access_token ?? process.env.REACT_APP_SUPABASE_ANON_KEY) : process.env.REACT_APP_SUPABASE_ANON_KEY;
  } catch { return process.env.REACT_APP_SUPABASE_ANON_KEY; }
}

function apiFetch(path) {
  const url = process.env.REACT_APP_SUPABASE_URL;
  const key = process.env.REACT_APP_SUPABASE_ANON_KEY;
  return fetch(`${url}/rest/v1/${path}`, {
    headers: { apikey: key, Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
  });
}

function formatDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

const ACTION_META = {
  login:               { color: '#15803d', bg: '#f0fdf4', border: '#bbf7d0', icon: 'bi-box-arrow-in-right' },
  logout:              { color: '#64748b', bg: '#f1f5f9', border: '#e2e8f0', icon: 'bi-box-arrow-right' },
  impersonate:         { color: '#d97706', bg: '#fffbeb', border: '#fde68a', icon: 'bi-person-badge' },
  create_tenant:       { color: '#1d4ed8', bg: '#dbeafe', border: '#bfdbfe', icon: 'bi-buildings' },
  delete_user:         { color: '#b91c1c', bg: '#fee2e2', border: '#fecaca', icon: 'bi-person-x' },
  update_role:         { color: '#7c3aed', bg: '#f3e8ff', border: '#e9d5ff', icon: 'bi-person-gear' },
  update_subscription: { color: '#0891b2', bg: '#ecfeff', border: '#a5f3fc', icon: 'bi-credit-card' },
  export_data:         { color: '#059669', bg: '#ecfdf5', border: '#a7f3d0', icon: 'bi-download' },
  password_reset:      { color: '#d97706', bg: '#fffbeb', border: '#fde68a', icon: 'bi-key' },
};

function ActionBadge({ action }) {
  const m = ACTION_META[action] ?? { color: '#64748b', bg: '#f1f5f9', border: '#e2e8f0', icon: 'bi-circle' };
  return (
    <span className="adm-badge" style={{ background: m.bg, color: m.color, borderColor: m.border, gap: 5 }}>
      <i className={`bi ${m.icon}`} style={{ fontSize: '0.7rem' }} />
      {action?.replace(/_/g, ' ') ?? '—'}
    </span>
  );
}

function exportCSV(logs) {
  const headers = ['Timestamp', 'Actor', 'Action', 'Target', 'Tenant', 'IP Address', 'Result'];
  const rows = logs.map(l => [
    formatDateTime(l.created_at),
    l.actor ?? '—',
    l.action ?? '—',
    l.target ?? '—',
    l.tenant ?? l.organization_id ?? '—',
    l.ip_address ?? '—',
    l.result ?? 'success',
  ]);
  const csv = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url;
  a.download = `audit-trail-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/* ──────────────────────────────────────────────
   Main Component
────────────────────────────────────────────── */
const AuditTrails = () => {
  const [logs, setLogs]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [filters, setFilters]   = useState({ actor: '', action: '', tenant: '' });
  const [page, setPage]         = useState(0);

  const PAGE_SIZE = 25;

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await apiFetch('audit_logs?select=*&order=created_at.desc&limit=500');
        if (res.ok) {
          setLogs(await res.json());
        } else {
          /* Table may not exist yet — show empty state */
          setLogs([]);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filtered = useMemo(() => {
    const { actor, action, tenant } = filters;
    return logs.filter(l => {
      const matchActor  = !actor  || (l.actor  ?? '').toLowerCase().includes(actor.toLowerCase());
      const matchAction = !action || l.action === action;
      const matchTenant = !tenant || (l.tenant ?? l.organization_id ?? '').toLowerCase().includes(tenant.toLowerCase());
      return matchActor && matchAction && matchTenant;
    });
  }, [logs, filters]);

  const paginated = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);

  const setFilter = (key, value) => { setFilters(p => ({ ...p, [key]: value })); setPage(0); };

  const uniqueActions = useMemo(() => [...new Set(logs.map(l => l.action).filter(Boolean))].sort(), [logs]);

  return (
    <div className="adm-container">

      <div className="adm-page-header">
        <h1 className="adm-page-title"><i className="bi bi-journal-text" />Audit Trails</h1>
        <p className="adm-page-subtitle">Immutable log of all administrative and user actions across the platform.</p>
      </div>
      <hr className="adm-divider" />

      {/* Stats */}
      <div className="adm-stats-row">
        {[
          { label: 'Total Events',    value: logs.length,                                           icon: 'bi-journal-check',   color: '#006B54' },
          { label: 'Unique Actors',   value: new Set(logs.map(l => l.actor).filter(Boolean)).size,  icon: 'bi-people',           color: '#1d4ed8' },
          { label: 'Filtered Events', value: filtered.length,                                       icon: 'bi-funnel',           color: '#0891b2' },
          { label: 'Action Types',    value: uniqueActions.length,                                  icon: 'bi-list-stars',       color: '#7c3aed' },
        ].map(s => (
          <div key={s.label} className="adm-stat-card" style={{ borderTopColor: s.color }}>
            <div className="adm-stat-icon" style={{ background: s.color + '18', color: s.color }}>
              <i className={`bi ${s.icon}`} />
            </div>
            <div className="adm-stat-body">
              <div className="adm-stat-value">{loading ? '—' : s.value}</div>
              <div className="adm-stat-label">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="adm-card" style={{ marginBottom: 18 }}>
        <div className="adm-card-header">
          <span className="adm-card-title">Filters</span>
          {(filters.actor || filters.action || filters.tenant) && (
            <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => setFilters({ actor: '', action: '', tenant: '' })}>
              <i className="bi bi-x-circle" /> Clear
            </button>
          )}
        </div>
        <div className="adm-card-body">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
            <div className="adm-form-group" style={{ margin: 0 }}>
              <label className="adm-label">Actor (email / ID)</label>
              <div className="adm-search-wrap">
                <i className="bi bi-search adm-search-icon" />
                <input
                  className="adm-input"
                  style={{ paddingLeft: 30 }}
                  placeholder="Filter by actor…"
                  value={filters.actor}
                  onChange={e => setFilter('actor', e.target.value)}
                />
              </div>
            </div>
            <div className="adm-form-group" style={{ margin: 0 }}>
              <label className="adm-label">Action</label>
              <select
                className="adm-select"
                value={filters.action}
                onChange={e => setFilter('action', e.target.value)}
              >
                <option value="">All Actions</option>
                {(uniqueActions.length > 0 ? uniqueActions : Object.keys(ACTION_META)).map(a => (
                  <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </div>
            <div className="adm-form-group" style={{ margin: 0 }}>
              <label className="adm-label">Tenant</label>
              <div className="adm-search-wrap">
                <i className="bi bi-search adm-search-icon" />
                <input
                  className="adm-input"
                  style={{ paddingLeft: 30 }}
                  placeholder="Filter by tenant…"
                  value={filters.tenant}
                  onChange={e => setFilter('tenant', e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="adm-card">
        <div className="adm-card-header">
          <span className="adm-card-title">
            Audit Log
            {!loading && filtered.length > 0 && (
              <span style={{ fontWeight: 400, color: '#94a3b8', fontSize: '0.8rem', marginLeft: 8 }}>
                {filtered.length} event{filtered.length !== 1 ? 's' : ''}
              </span>
            )}
          </span>
          <button
            className="adm-btn adm-btn--outline adm-btn--sm"
            onClick={() => exportCSV(filtered)}
            disabled={filtered.length === 0}
          >
            <i className="bi bi-download" /> Export CSV
          </button>
        </div>
        <div className="adm-card-body--flush">
          {error && (
            <div className="adm-alert adm-alert--danger" style={{ margin: 16 }}>
              <i className="bi bi-exclamation-circle" />{error}
            </div>
          )}
          {loading ? (
            <div className="adm-empty"><div className="adm-spinner" /><span className="adm-empty-text">Loading audit records…</span></div>
          ) : paginated.length === 0 ? (
            <div className="adm-empty">
              <i className="bi bi-journal-x" />
              <span className="adm-empty-text">
                {logs.length === 0
                  ? 'No audit records yet. Events will appear here as users and admins perform actions.'
                  : 'No records match your current filters.'}
              </span>
            </div>
          ) : (
            <div className="adm-table-wrap">
              <table className="adm-table">
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>Actor</th>
                    <th>Action</th>
                    <th>Target</th>
                    <th>Tenant</th>
                    <th>IP</th>
                    <th>Result</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((l, i) => (
                    <tr key={l.id ?? i}>
                      <td style={{ fontSize: '0.78rem', color: '#64748b', whiteSpace: 'nowrap' }}>{formatDateTime(l.created_at)}</td>
                      <td style={{ fontSize: '0.82rem', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.actor ?? '—'}</td>
                      <td><ActionBadge action={l.action} /></td>
                      <td style={{ fontSize: '0.82rem', color: '#4a5a54', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.target ?? '—'}</td>
                      <td style={{ fontSize: '0.78rem', color: '#64748b' }}>{l.tenant ?? l.organization_id ?? '—'}</td>
                      <td style={{ fontSize: '0.75rem', color: '#94a3b8', fontFamily: 'monospace' }}>{l.ip_address ?? '—'}</td>
                      <td>
                        <span className={`adm-badge adm-badge--${l.result === 'failure' ? 'red' : 'green'}`}>
                          {l.result === 'failure' ? 'Failed' : 'Success'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', borderTop: '1px solid #f1f5f4' }}>
            <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
              Page {page + 1} of {totalPages} · {filtered.length} records
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => setPage(p => p - 1)} disabled={page === 0}>
                <i className="bi bi-chevron-left" />
              </button>
              <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}>
                <i className="bi bi-chevron-right" />
              </button>
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default AuditTrails;
